import * as React from "react";
import {useEffect, useState} from "react";
import {Box, Button, Divider, Drawer, IconButton, TextField, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {SaveDailyReportDiagnosisReactQuery} from "./reactQuery/DailyReportReactQuery";

export interface DailyReportDetailsRow {
    powerStationId: number;
    powerStationName: string;
    generatedEnergy: string;
    expectedEnergy: string;
    irradiance: string;
    performanceRatio: string;
    operationAvailability: string;
    diagnosis: string;
    details?: string | null;
}

interface Props {
    open: boolean;
    row: DailyReportDetailsRow | null;
    dateTime: string | null;
    onClose: () => void;
    onAfterSave?: () => void;
}

const DIAGNOSIS_MAX_LENGTH = 150;
const DETAILS_MAX_LENGTH = 1500;
const INVALID_CHARACTER = ";";
const INVALID_CHARACTER_MESSAGE = "Caracter inválido. Não é permitido uso de ; no preenchimento deste campo";

export default function DailyReportDetailsDrawer({open, row, dateTime, onClose, onAfterSave}: Props) {
    const [description, setDescription] = useState<string>("");
    const [details, setDetails] = useState<string>("");
    const mutationSave = SaveDailyReportDiagnosisReactQuery();

    useEffect(() => {
        if (open) {
            setDescription(row?.diagnosis ?? "");
            setDetails(row?.details ?? "");
        }
    }, [open, row]);

    const diagnosisExceededLength = description.length > DIAGNOSIS_MAX_LENGTH;
    const diagnosisContainsSemicolon = description.includes(INVALID_CHARACTER);
    const diagnosisHasError = diagnosisExceededLength || diagnosisContainsSemicolon;
    const diagnosisCounterColor = diagnosisExceededLength ? "error.main" : "text.secondary";

    const detailsExceededLength = details.length > DETAILS_MAX_LENGTH;
    const detailsHasError = detailsExceededLength;
    const detailsCounterColor = detailsExceededLength ? "error.main" : "text.secondary";

    const hasError = diagnosisHasError || detailsHasError;

    const handleSave = () => {
        if (!row || !dateTime || hasError) return;
        mutationSave.mutate(
            {
                powerStationId: row.powerStationId,
                dateTime,
                description,
                details,
            },
            {
                onSuccess: () => {
                    onAfterSave?.();
                    onClose();
                },
            }
        );
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{zIndex: (theme) => theme.zIndex.modal + 2}}
            PaperProps={{
                sx: {
                    width: {xs: "100%", sm: 480},
                    borderTopLeftRadius: 12,
                    borderBottomLeftRadius: 12,
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            <Box sx={{px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <Typography sx={{fontWeight: 500, fontSize: 24}}>Detalhes</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </Box>

            <Divider/>

            <Box sx={{px: 2.5, py: 2, flex: 1, overflow: "auto"}}>
                <Typography sx={{fontWeight: 500, mb: 1, fontSize: 20}}>Informações Gerais</Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        rowGap: 1,
                        columnGap: 2,
                        mb: 2,
                        "& .label": {color: "text.secondary", fontSize: 14},
                        "& .value": {fontSize: 14, fontWeight: 600, textAlign: "right"},
                    }}
                >
                    <Typography className="label">Usina</Typography>
                    <Typography className="value">{row?.powerStationName ?? "-"}</Typography>

                    <Typography className="label">Energia Gerada (MWh)</Typography>
                    <Typography className="value">{row?.generatedEnergy ?? "-"}</Typography>

                    <Typography className="label">Energia Esperada (MWh)</Typography>
                    <Typography className="value">{row?.expectedEnergy ?? "-"}</Typography>

                    <Typography className="label">Irradiação POA (kWh/m²)</Typography>
                    <Typography className="value">{row?.irradiance ?? "-"}</Typography>

                    <Typography className="label">PR</Typography>
                    <Typography className="value">{row?.performanceRatio ?? "-"}</Typography>

                    <Typography className="label">Disponibilidade</Typography>
                    <Typography className="value">{row?.operationAvailability ?? "-"}</Typography>
                </Box>

                <Divider sx={{my: 2}}/>

                <Typography sx={{fontWeight: 500, mb: 1, fontSize: 20}}>Diagnóstico</Typography>

                <TextField
                    multiline
                    minRows={3}
                    fullWidth
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    error={diagnosisHasError}
                    helperText={diagnosisContainsSemicolon ? INVALID_CHARACTER_MESSAGE : " "}
                    inputProps={{"aria-label": "Diagnóstico"}}
                />

                <Box sx={{display: "flex", justifyContent: "flex-end", mt: 0.5, mb: 2}}>
                    <Typography sx={{fontSize: 12, color: diagnosisCounterColor, fontWeight: diagnosisExceededLength ? 600 : 400}}>
                        {description.length}/{DIAGNOSIS_MAX_LENGTH}
                    </Typography>
                </Box>

                <Typography sx={{fontWeight: 500, mb: 1, fontSize: 20}}>Detalhes</Typography>

                <TextField
                    multiline
                    minRows={3}
                    fullWidth
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    error={detailsHasError}
                    helperText=" "
                    inputProps={{"aria-label": "Detalhes"}}
                />

                <Box sx={{display: "flex", justifyContent: "flex-end", mt: 0.5}}>
                    <Typography sx={{fontSize: 12, color: detailsCounterColor, fontWeight: detailsExceededLength ? 600 : 400}}>
                        {details.length}/{DETAILS_MAX_LENGTH}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{px: 2.5, py: 2, borderTop: "1px solid", borderColor: "divider"}}>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSave}
                    disabled={hasError || mutationSave.isLoading || !row || !dateTime}
                    sx={{borderRadius: 1, fontWeight: 700, mb: 1}}
                >
                    SALVAR ALTERAÇÕES
                </Button>
                <Button
                    fullWidth
                    variant="text"
                    onClick={onClose}
                    sx={{fontWeight: 700}}
                >
                    VOLTAR
                </Button>
            </Box>
        </Drawer>
    );
}
